import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { PreventionGroupHeaderComponent } from '../../component/prevention-group-header/prevention-group-header.component';
import {
  PreventionGroupService,
  PreventionGroupDashboard,
  GroupNeedCoverageView,
  PatientDemandDashboardView,
  GroupSiteRow,
  GroupOfferRow,
} from '../../core/services/prevention.service';

/**
 * "À retenir" Vue d'ensemble (Lot Prevention First UX Phase 1, §2) -- règle déterministe combinant
 * 3 sources DÉJÀ chargées par ailleurs (Coverage/`getNeedsDashboard`, Patient Demand/
 * `getDemandDashboard`, tous deux réutilisés ICI en plus de `getDashboard()` -- jamais une nouvelle
 * API, voir javadoc de la classe) : besoin principal non couvert > sujet à potentiel sans offre >
 * site à surveiller > repli positif. Maximum 3, dans cet ordre, chaque type sauté s'il n'a aucun
 * candidat réel (jamais un insight fabriqué).
 */
type OverviewInsight =
  | { kind: 'topUncovered'; needLabel: string; uncoveredCount: number; uncoveredSiteCount: number }
  | { kind: 'topicToExplore'; topicLabel: string; interestedSessions: number }
  | { kind: 'siteToWatch'; siteName: string; completionRate: number }
  | { kind: 'goodCoverage'; rate: number };

/**
 * Groupe > Prévention > Vue d'ensemble (Lot Prevention First UX Phase 1) -- porte d'entrée DG :
 * synthèse + orientation, jamais le détail complet (celui-ci reste dans les 3 pages spécialisées).
 * <p>3 appels réutilisant des endpoints EXISTANTS (aucune nouvelle API) : {@code getDashboard}
 * (KPI/funnel/sites/offres, déjà utilisé ici), {@code getNeedsDashboard}/{@code getDemandDashboard}
 * (déjà utilisés par "Besoins de prévention"/"Campagne de prévention", réutilisés ICI uniquement
 * pour alimenter "À retenir" et les cartes "Où agir ?" avec des chiffres réels -- jamais un second
 * calcul indépendant). Un échec de {@code getNeedsDashboard}/{@code getDemandDashboard} dégrade
 * seulement les insights concernés (jamais toute la page, ces 2 appels sont annexes ici).
 */
/** Site dont le backend expose des totaux : exclu du classement dès que ses totaux sont retenus (jamais un taux fictif) ou trop petits. */
type ReliableSite = GroupSiteRow & { started: number; completionRate: number };
function isReliableSite(site: GroupSiteRow, threshold: number): boolean {
  return site.insufficientData !== true && site.started != null && site.completionRate != null && site.started >= threshold;
}

@Component({
  selector: 'app-pilotage-prevention',
  standalone: true,
  imports: [TranslatePipe, PreventionGroupHeaderComponent],
  templateUrl: './pilotage-prevention.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pilotage-prevention.component.scss'],
})
export class PilotagePreventionComponent {
  private readonly groupSvc = inject(PreventionGroupService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /** Restaure la période choisie avant un drill-down vers un site (voir openSite/`?days=`), jamais
   *  un état persisté au-delà de cet aller-retour -- une visite directe de la page reste à 30 j. */
  readonly periodDays = signal(this.readInitialPeriodDays());

  private readInitialPeriodDays(): number {
    const raw = Number(this.route.snapshot.queryParamMap.get('days'));
    return [7, 30, 90].includes(raw) ? raw : 30;
  }

  readonly loadError = signal(false);

  readonly dashboard = toSignal(
    toObservable(this.periodDays).pipe(
      tap(() => this.loadError.set(false)),
      switchMap(days => this.groupSvc.getDashboard(days).pipe(
        catchError(() => { this.loadError.set(true); return of(null); })
      ))
    ),
    { initialValue: null as PreventionGroupDashboard | null }
  );

  /** Réutilisé de "Besoins de prévention" -- ageBand jamais filtré ici (vue Groupe non segmentée). */
  readonly needsDashboard = toSignal(
    toObservable(this.periodDays).pipe(
      switchMap(days => this.groupSvc.getNeedsDashboard(days, null).pipe(catchError(() => of(null))))
    ),
    { initialValue: null as GroupNeedCoverageView | null }
  );

  /** Réutilisé de "Campagne de prévention". */
  readonly demandDashboard = toSignal(
    toObservable(this.periodDays).pipe(
      switchMap(days => this.groupSvc.getDemandDashboard(days).pipe(catchError(() => of(null))))
    ),
    { initialValue: null as PatientDemandDashboardView | null }
  );

  setPeriodDays(days: number): void {
    this.periodDays.set(days);
  }

  /**
   * Seuil small-cohort (Lot Prevention First UX Phase 1, review avant GO) -- réutilise EXACTEMENT
   * la même valeur que "Besoins de prévention"/"Campagne de prévention" (déjà chargée via
   * `needsDashboard()`, jamais un second appel ni une constante dupliquée). {@code sites[]}/
   * {@code offers[]} (issus de `getDashboard()`) n'ont AUCUN masquage small-cohort côté backend --
   * ce seuil est appliqué ICI, uniquement pour écarter un site/une offre à trop faible volume de la
   * sélection "meilleur"/"à surveiller"/"à optimiser" (jamais pour masquer une valeur affichée
   * ailleurs sur cette même page, ex. le tableau -- il n'y en a plus sur cette page).
   */
  readonly smallCohortThreshold = computed(() => this.needsDashboard()?.dataQuality.smallCohortThreshold ?? 10);

  /**
   * Drill-down Groupe -> Site (Ajustement UX/navigation, 2026-09-07) -- réutilise la page EXISTANTE
   * Site > Prévention (jamais une seconde Vue Site), via la route dédiée `pilotage-prevention/sites/
   * :id` (mode {@code groupContext}, voir SiteComponent) plutôt que la route standalone `/site` :
   * sélecteur de site masqué, breadcrumb Groupe, retour explicite ici. `selectEstablishment` n'est
   * plus appelé ici -- SiteComponent le fait lui-même à l'entrée en mode groupe, jamais un double
   * appel. La période courante est transmise en query param pour que "Retour" la restaure.
   */
  openSite(establishmentId: string): void {
    this.router.navigate(['/pilotage-prevention/sites', establishmentId], { queryParams: { days: this.periodDays() } });
  }

  goToNeeds(): void {
    this.router.navigate(['/pilotage-prevention/besoins']);
  }

  goToCampaign(): void {
    this.router.navigate(['/pilotage-prevention/demande']);
  }

  goToOffers(): void {
    this.router.navigate(['/prevention-offres']);
  }

  formatRate(rate: number | null | undefined): string {
    if (rate == null) return '—';
    return Math.round(rate * 100) + '%';
  }

  formatPrice(value: number | null | undefined, currency: string | null | undefined): string {
    if (value == null) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency || 'EUR', maximumFractionDigits: 0 }).format(value);
  }

  // ───────────────────────── À retenir (§2, max 3) ─────────────────────────

  readonly insights = computed<OverviewInsight[]>(() => {
    const result: OverviewInsight[] = [];

    const needs = this.needsDashboard();
    if (needs && needs.detectedNeedCount != null) {
      const visible = (needs.needs ?? []).filter(n => !n.insufficientData);
      const top = visible.reduce<typeof visible[number] | null>((best, n) => {
        const bestUncovered = best?.uncoveredCount ?? 0;
        const nUncovered = n.uncoveredCount ?? 0;
        return nUncovered > bestUncovered ? n : best;
      }, null);
      if (top && (top.uncoveredCount ?? 0) > 0) {
        result.push({
          kind: 'topUncovered', needLabel: top.needLabel,
          uncoveredCount: top.uncoveredCount!, uncoveredSiteCount: top.uncoveredSiteCount ?? 0,
        });
      }
    }

    const demand = this.demandDashboard();
    if (demand && demand.exposedSessionsTotal != null) {
      const candidate = (demand.topics ?? [])
        .filter(t => !t.insufficientData && t.denominatorReliable && (t.interestedSessions ?? 0) > 0 && t.offerAvailable === false)
        .reduce<typeof demand.topics[number] | null>((best, t) =>
          (t.interestedSessions ?? 0) > (best?.interestedSessions ?? 0) ? t : best, null);
      if (candidate) {
        result.push({ kind: 'topicToExplore', topicLabel: candidate.topicLabel, interestedSessions: candidate.interestedSessions! });
      }
    }

    if (result.length < 3) {
      // Exclut les sites sous le seuil small-cohort : un site à 1 seul parcours démarré (0% ou 100%
      // de complétion) n'est jamais un signal fiable, jamais mis en avant comme "à surveiller".
      const threshold = this.smallCohortThreshold();
      const sites = (this.dashboard()?.sites ?? []).filter((s): s is ReliableSite => isReliableSite(s, threshold));
      const worst = sites.reduce<ReliableSite | null>((best, s) =>
        (best == null || s.completionRate < best.completionRate) ? s : best, null);
      if (worst) {
        result.push({ kind: 'siteToWatch', siteName: worst.establishmentName, completionRate: worst.completionRate });
      }
    }

    if (result.length < 3 && needs && needs.needCoverageRate != null) {
      result.push({ kind: 'goodCoverage', rate: needs.needCoverageRate });
    }

    return result.slice(0, 3);
  });

  // ───────────────────────── Où agir ? (§2) ─────────────────────────

  readonly uncoveredNeedsCount = computed<number | null>(() => this.needsDashboard()?.uncoveredCount ?? null);
  readonly topicsToExploreCount = computed<number | null>(() => this.demandDashboard()?.topicsWithoutOfferCount ?? null);
  readonly trackedOffersCount = computed<number>(() => this.dashboard()?.offers.length ?? 0);

  // ───────────────────────── Performance réseau (§2, résumé uniquement) ─────────────────────────

  readonly networkPerformance = computed<{
    bestSite: GroupSiteRow | null; siteToWatch: GroupSiteRow | null;
    bestOffer: GroupOfferRow | null; offerToOptimize: GroupOfferRow | null;
  }>(() => {
    // Même seuil small-cohort que le reste de Prévention (review avant GO) -- sites[]/offers[]
    // n'ont aucun masquage backend, donc "meilleur"/"à surveiller"/"à optimiser" doivent écarter eux-
    // mêmes tout volume trop faible pour être un signal fiable (jamais un site à 1 parcours démarré).
    const threshold = this.smallCohortThreshold();
    const sites = (this.dashboard()?.sites ?? []).filter((s): s is ReliableSite => isReliableSite(s, threshold));
    const offers = (this.dashboard()?.offers ?? []).filter(o => o.recommendationReached >= threshold);

    const bestSite = sites.reduce<ReliableSite | null>((best, s) =>
      (best == null || s.completionRate > best.completionRate) ? s : best, null);
    const worstSite = sites.reduce<ReliableSite | null>((worst, s) =>
      (worst == null || s.completionRate < worst.completionRate) ? s : worst, null);
    const bestOffer = offers.reduce<GroupOfferRow | null>((best, o) =>
      (best == null || o.opportunityValue > best.opportunityValue) ? o : best, null);
    const worstOffer = offers.reduce<GroupOfferRow | null>((worst, o) =>
      (worst == null || o.interestRate < worst.interestRate) ? o : worst, null);

    // Un seul site/offre au total ne peut pas être à la fois "meilleur" et "à surveiller" -- jamais
    // la même ligne affichée deux fois avec un jugement contradictoire.
    return {
      bestSite,
      siteToWatch: sites.length > 1 ? worstSite : null,
      bestOffer,
      offerToOptimize: offers.length > 1 ? worstOffer : null,
    };
  });
}
